/**
 * Экран «Чеки» — §14.2 (метрики `R9`–`R13`) и последний шаг раскрытия
 * §14.3.
 *
 * Два слоя на одном экране, и это не сборная солянка: сверху — какие
 * чеки бывают (средний, на гостя, наполняемость, распределение), снизу —
 * какие чеки были. Второе доказывает первое, и разносить их по разным
 * экранам значило бы разорвать ровно ту цепочку, ради которой §14.3
 * написан.
 *
 * **Список чеков — конец цепочки, а не отдельный отчёт.** Он открывается
 * с фильтрами, пришедшими из «Обзора» (день) или «Продаж» (категория,
 * блюдо, официант), и показывает те же чеки, что вошли в раскрытую
 * цифру. Совпадение обеспечено не аккуратностью, а тем, что отбор на
 * сервере — тот же самый `CHECK_FILTER`, что у метрик.
 *
 * **Аннулированные и удалённые сюда не попадают** — как и в выручку
 * (§9.1). Но если человек дошёл до конкретного чека по ссылке и тот
 * оказался аннулированным, карточка это **покажет**: он должен понять,
 * почему чека нет в сумме, а не решить, что цифра врёт.
 */

import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@shell/ui';
import { Empty, ErrorState, Loading } from '@shell/states';
import { formatMoney, formatNumber, formatShortDateTime } from '@shell/format';
import { financeApi } from '../api';
import type { CheckDetail, CheckList, MetricView, PeriodParams } from '../api';
import { Breakdown } from '../charts/Charts';
import type { BreakdownRow } from '../charts/Charts';
import {
  DownloadLink,
  ExplanationModal,
  FreshnessBadge,
  MetricTile,
  PeriodPicker,
  ScreenError,
  Section,
  useExplanation,
} from '../components';

const TILE_CODES = ['check.average', 'check.per_guest', 'check.depth', 'check.structure'];

/** Сколько чеков видно сразу и сколько добавляет «Показать ещё». Сервер
 *  отдаёт по двести: на телефоне это десять экранов прокрутки, а в Mini
 *  App прокрутка вниз ещё и закрывала приложение (владелец, 11.09.2026). */
const STEP = 20;

export interface CheckFilters {
  waiter?: string;
  category?: string;
  item?: string;
}

/**
 * Подпись действующего отбора — человек должен видеть, что список сужен.
 *
 * Названия приходят с сервера: в адресе лежат коды кассы, и подпись
 * «чеки, где есть категория 1003» человеку не говорила ничего. Нет
 * названия — код как есть: пустое место было бы хуже.
 */
function filterLabel(
  filters: CheckFilters,
  labels: CheckList['filter_labels'] | undefined,
): string | null {
  const parts: string[] = [];
  if (filters.category) parts.push(`категория «${labels?.category ?? filters.category}»`);
  if (filters.item) parts.push(`блюдо «${labels?.item ?? filters.item}»`);
  if (filters.waiter) parts.push(`официант ${labels?.waiter ?? filters.waiter}`);
  return parts.length > 0 ? parts.join(', ') : null;
}

function money(value: string | null): string {
  if (value === null) return '—';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? formatMoney(numeric) : value;
}

export function Checks({
  period,
  filters,
  onPeriodChange,
  onClearFilters,
}: {
  period: PeriodParams;
  filters: CheckFilters;
  onPeriodChange: (next: PeriodParams) => void;
  onClearFilters: () => void;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(true);
  const [shown, setShown] = useState(STEP);
  const [explained, explain, closeExplanation] = useExplanation();

  const metricQueries = useQueries({
    queries: [...TILE_CODES, 'check.distribution'].map((code) => ({
      queryKey: ['finance', 'metric', code, period],
      queryFn: () => financeApi.metric(code, period),
    })),
  });

  const listQuery = useQuery({
    queryKey: ['finance', 'checks', period, filters, offset],
    queryFn: () => financeApi.checks({ ...period, ...filters, offset }),
  });

  if (metricQueries.some((query) => query.isLoading) || listQuery.isLoading) {
    return <Loading />;
  }
  const failed = metricQueries.find((query) => query.error) ?? (listQuery.error ? listQuery : null);
  if (failed) {
    return (
      <ScreenError
        error={failed.error}
        period={period}
        anchor={period.to ?? null}
        onPeriodChange={onPeriodChange}
        onRetry={() => {
          metricQueries.forEach((query) => query.refetch());
          listQuery.refetch();
        }}
      />
    );
  }

  const metrics = metricQueries.map((query) => query.data as MetricView);
  const tiles = metrics.slice(0, TILE_CODES.length);
  const distribution = metrics[metrics.length - 1];
  const list = listQuery.data as CheckList;
  const label = filterLabel(filters, list.filter_labels);

  return (
    <div className="screen">
      {tiles[0]?.freshness && <FreshnessBadge freshness={tiles[0].freshness} />}
      <PeriodPicker period={period} anchor={period.to ?? null} onChange={onPeriodChange} />

      <div className="tiles">
        {tiles.map((metric) => (
          <MetricTile key={metric.code} metric={metric} onExplain={explain} />
        ))}
      </div>

      <Section
        title="Распределение чеков"
        actions={
          <>
            <Button small variant="quiet" onClick={() => explain(distribution)}>
              Как посчитано
            </Button>
            <DownloadLink href={financeApi.csvUrl('check.distribution', period)}>CSV</DownloadLink>
          </>
        }
      >
        <Breakdown
          title="Чеков в интервале"
          rows={distribution.rows as BreakdownRow[]}
          valueKey="checks"
          unit="шт"
        />
      </Section>

      <Section
        title={`Чеки: ${list.total}`}
        actions={
          <>
            {label && (
              <Button small variant="quiet" onClick={onClearFilters}>
                Снять отбор
              </Button>
            )}
            {list.checks.length > 0 && (
              <Button small variant="quiet" onClick={() => setOpen(!open)}>
                {open ? 'Свернуть' : 'Развернуть'}
              </Button>
            )}
          </>
        }
      >
        {label && (
          <p className="faint">
            Показаны только чеки, где есть {label}. Это те же чеки, что вошли в цифру, из
            которой вы сюда пришли.
          </p>
        )}

        {!open && list.checks.length > 0 ? (
          <p className="faint">
            Список свёрнут: {list.total} чеков. Чтобы увидеть их, нажмите «Развернуть».
          </p>
        ) : list.checks.length === 0 ? (
          <Empty
            title="Чеков нет"
            text="За выбранный период с этим отбором закрытых чеков не нашлось."
          />
        ) : (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Закрыт</th>
                    <th>Стол</th>
                    <th>Гостей</th>
                    <th>Официант</th>
                    <th className="num">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {list.checks.slice(0, shown).map((row) => (
                    <tr key={row.id}>
                      <td>
                        <button
                          type="button"
                          className="linkish"
                          onClick={() => setOpenId(row.id)}
                        >
                          {row.closed_at ? formatShortDateTime(row.closed_at) : row.business_date}
                        </button>
                      </td>
                      <td>{row.table ?? '—'}</td>
                      <td>{row.guests ?? '—'}</td>
                      <td>{row.waiter ?? '—'}</td>
                      <td className="num">{money(row.amount_net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {shown < list.checks.length && (
              <div className="pager">
                <Button small onClick={() => setShown(shown + STEP)}>
                  Показать ещё {Math.min(STEP, list.checks.length - shown)}
                </Button>
                <span className="faint">
                  Показано {shown} из {list.checks.length}
                  {list.total > list.checks.length ? ' на этой странице' : ''}
                </span>
              </div>
            )}

            {list.total > list.page && (
              <div className="pager">
                <Button
                  small
                  variant="quiet"
                  disabled={offset === 0}
                  onClick={() => {
                    setOffset(Math.max(0, offset - list.page));
                    setShown(STEP);
                  }}
                >
                  Назад
                </Button>
                <span className="faint">
                  {offset + 1}–{Math.min(offset + list.page, list.total)} из {list.total}
                </span>
                <Button
                  small
                  variant="quiet"
                  disabled={offset + list.page >= list.total}
                  onClick={() => {
                    setOffset(offset + list.page);
                    setShown(STEP);
                  }}
                >
                  Дальше
                </Button>
              </div>
            )}
          </>
        )}
      </Section>

      {openId !== null && <CheckCard id={openId} onClose={() => setOpenId(null)} />}
      <ExplanationModal metric={explained} onClose={closeExplanation} />
    </div>
  );
}

/**
 * Карточка чека — самый нижний уровень §14.3.
 *
 * Здесь заканчивается «докажи мне эту цифру»: позиции, оплаты, скидка.
 * Дальше идти некуда, и это правильная точка остановки — ниже уже
 * устройство кассы, а не финансы ресторана.
 */
function CheckCard({ id, onClose }: { id: number; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['finance', 'check', id],
    queryFn: () => financeApi.check(id),
  });

  return (
    <Modal title="Чек" onClose={onClose}>
      {query.isLoading && <Loading />}
      {query.error && <ErrorState error={query.error} onRetry={() => query.refetch()} />}
      {query.data && <CheckBody check={query.data as CheckDetail} />}
    </Modal>
  );
}

function CheckBody({ check }: { check: CheckDetail }) {
  return (
    <div className="check">
      {/* Признак исключения — первым делом. Человек пришёл сюда
          проверять цифру, и «этого чека нет в выручке» — главное, что
          ему надо знать, если это так. */}
      {(check.is_void || check.is_deleted) && (
        <p className="check__void" role="note">
          {check.is_void ? 'Чек аннулирован' : 'Чек удалён'} — в выручку он не входит (§9.1).
        </p>
      )}

      <dl className="check__head">
        <div>
          <dt>Закрыт</dt>
          <dd>{check.closed_at ? formatShortDateTime(check.closed_at) : '—'}</dd>
        </div>
        <div>
          <dt>Операционный день</dt>
          <dd>{check.business_date}</dd>
        </div>
        <div>
          <dt>Стол</dt>
          <dd>{check.table ?? '—'}</dd>
        </div>
        <div>
          <dt>Гостей</dt>
          <dd>{check.guests ?? '—'}</dd>
        </div>
        <div>
          <dt>Официант</dt>
          <dd>{check.waiter ?? '—'}</dd>
        </div>
      </dl>

      <table className="table">
        <thead>
          <tr>
            <th>Позиция</th>
            <th className="num">Кол-во</th>
            <th className="num">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {check.items.map((item, index) => (
            <tr key={index} className={item.is_deleted ? 'row--muted' : undefined}>
              <td>
                {item.is_modifier ? '· ' : ''}
                {item.name}
                {item.is_deleted && <span className="faint"> (удалена)</span>}
              </td>
              <td className="num">{formatNumber(Number(item.quantity ?? 0), 0)}</td>
              <td className="num">{money(item.amount_net)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Скидка</th>
            <td />
            <td className="num">{money(check.amount_discount)}</td>
          </tr>
          <tr>
            <th>Итого</th>
            <td />
            <td className="num">
              <strong>{money(check.amount_net)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>

      <h3 className="check__subtitle">Оплата</h3>
      <ul className="check__payments">
        {check.payments.length === 0 && <li className="faint">Оплат в базе нет</li>}
        {check.payments.map((payment, index) => (
          <li key={index}>
            {payment.name} — {money(payment.amount)}
          </li>
        ))}
      </ul>
    </div>
  );
}
